import React, { useEffect, useState } from 'react';
import {
  App, Button, Form, Tag,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { useMessageError } from '../../../../hooks/common';
import SinglePageContainer from '../../../Common/SinglePageContainer';
import {
  UserTableRow, useUserGetById, useUserUpdate,
} from '../../../../hooks/api/users';
import { AdminCreateParams, AdminUpdateParams } from '../../../../hooks/api/admins';

import {
  DeactivateButton, DeleteButton, ResetPasswordButton,
} from '../actions';
import { useUnsavedChanges } from '../../../../context/unsavedChanges';
import { isObjEqual } from '../../../../utils';
import { useAuth, User } from '../../../../store/auth';
import { UserRole } from '../../../../enums/user';
import AdminFormItems from '../Form';
import { getUserStatusColor } from '../index';

const initialState: AdminUpdateParams = {
  firstName: '',
  lastName: '',
  status: 'active',
  email: '',
  role: UserRole.EDITOR,

};

const prepareValues = (data: User): AdminUpdateParams => {
  const {
    status, email, role, firstName, lastName, isTwoFAActive, // firstName, phone, dateOfBirth,
  } = data;

  return {
    firstName,
    lastName,
    status,
    email,
    role,
    isTwoFAActive,
  };
};

export default function AdminUpdate(): React.ReactNode {
  const { id = '' } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { open, contextHolder } = useSimpleModal();
  const [form] = Form.useForm<AdminCreateParams>();

  const userById = useUserGetById(id, undefined, 'admins');
  const userUpdate = useUserUpdate<AdminUpdateParams>(id, 'admins');

  const extendedUserRes: UserTableRow | null = userById.data ? {
    key: userById.data.id,
    fullName: `${userById.data.firstName}`,
    ...userById.data,
  } : null;

  /** Initial Values >>> */
  const { unsavedChanges, handleUnsavedChanges } = useUnsavedChanges();
  const [initialValues, setInitialValues] = useState(initialState); // <User>

  useEffect(() => {
    if (userById.data && !userById.loading && !userById.error) {
      setInitialValues(prepareValues(userById.data));
    }
  }, [userById.data]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    handleUnsavedChanges(false);
  }, [initialValues]);
  /** <<< Initial Values */

  const handleSubmit = (values: AdminCreateParams) => {
    /** Email should not be sent on update */
    form.validateFields().then(({ email, ...val }) => {
      /** If the admin turns on their own 2FA, they need to relogin to bind an authenticator app */
      const isEnablingOwnTwoFA = currentUser?.id === id
        && !initialValues.isTwoFAActive
        && (val as AdminUpdateParams).isTwoFAActive;

      const regularUserUpdate = () => (
        userUpdate.fetch({
          ...val,
          status: undefined,
        }, id)
          .then((res) => {
            if (res?.id) {
              if (isEnablingOwnTwoFA) {
                message.success('Success! Now relogin to connect your authenticator app.', 6);
              } else {
                message.success('Changes saved successfully');
              }
              setInitialValues(prepareValues(res));
            }
          }));

      handleUnsavedChanges(false);
      regularUserUpdate();
    });
  };

  useMessageError([userById, userUpdate]);

  const handleFieldsChange = () => {
    if (!initialValues) return;
    handleUnsavedChanges(!isObjEqual(
      initialValues,
      form.getFieldsValue(),
    ));
  };

  return (
    <SinglePageContainer
      title="Update admin"
      breadcrumbItems={[]}
      stickyHeader
      cardLoading={userById.loading}
      extra={(
        <>
          {initialValues.status ? (
            <Tag color={getUserStatusColor(initialValues.status)} className="capitalize-first-letter">
              {initialValues.status}
            </Tag>
          ) : null}
          {extendedUserRes ? ( // userById.data
            <>
              <ResetPasswordButton
                record={extendedUserRes}
                buttonProps={{ type: 'default', children: 'Reset password' }}
                afterSuccess={(res) => (res ? setInitialValues(prepareValues(res)) : undefined)}
              />
              <DeactivateButton
                record={{ ...extendedUserRes, status: initialValues.status || extendedUserRes.status }}
                buttonProps={{ type: 'default' }}
                afterSuccess={(res) => (res ? setInitialValues(prepareValues(res)) : undefined)}
              />
              <DeleteButton
                record={extendedUserRes}
                buttonProps={{ type: 'default' }}
                afterSuccess={() => navigate('/admins')}
              />
            </>
          ) : null}
          <Button
            type="primary"
            onClick={form.submit}
            icon={<SaveOutlined />}
            loading={userUpdate.loading || userById.loading}
            disabled={!unsavedChanges}
          >
            Save
          </Button>
        </>
      )}
    >
      {contextHolder}
      <Form
        form={form}
        name="admin-form"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        onFieldsChange={handleFieldsChange}
      >
        <AdminFormItems data={initialValues} variant="update" editedUserId={id} />
      </Form>

    </SinglePageContainer>
  );
}
